/**
 * Utility functions for pole length calculation and size mapping
 * Extracted from PoleConfigurator for reuse in inline calculator
 */

/**
 * Calculate pole length based on bag dimensions, pole type, loop configuration
 * @param {Object} params - Calculation parameters
 * @param {string} params.poleType - Type of pole (Lockhead, Springhead, etc.)
 * @param {number} params.bagHeight - Bag height in cm
 * @param {number} params.bagLength - Bag length in cm
 * @param {number} params.bagWidth - Bag width in cm
 * @param {string} params.loopType - Type of loop (Corner loop, Cross corner loop)
 * @param {number} params.loopLength - Loop length in cm
 * @returns {Object} Result object with poleLength, error, and details
 */
export const calculatePoleLength = ({ poleType, bagHeight, bagLength, bagWidth, loopType, loopLength }) => {
  try {
    // Input validation
    if (!poleType) {
      throw new Error("Pole type must be selected");
    }

    const heightCm = parseFloat(bagHeight) || 0;
    const length = parseFloat(bagLength) || 0;
    const width = parseFloat(bagWidth) || 0;
    const loopLengthNum = parseFloat(loopLength) || 0;

    // Basic validation
    if (heightCm < 1 || length < 1 || width < 1 || loopLengthNum < 1) {
      throw new Error("Please enter valid positive dimensions and loop length.");
    }

    // Loop length bounds validation
    if (loopLengthNum < 15 || loopLengthNum > 50) {
      throw new Error(`Loop length must be between 15cm and 50cm. Provided: ${loopLengthNum}cm.`);
    }

    const baseHeight = heightCm * 10; // Convert cm to mm
    let poleLength;
    let details = [];

    // Check if it's Flow or standard pole type
    if (poleType.toLowerCase().includes('flow')) {
      // Flow pole calculation
      if (length < 95 || length > 100 || width < 95 || width > 100) {
        throw new Error(`Flow pole requires bag dimensions between 95x95 and 100x100. Provided: ${length}x${width}.`);
      }

      const loopLengthMm = loopLengthNum * 10;
      if (loopLengthMm < 200) {
        throw new Error(`Flow pole requires loop length of at least 20cm. Provided: ${loopLengthNum}cm.`);
      }

      poleLength = baseHeight + loopLengthMm - 100;
      details = [
        `Base height: ${baseHeight}mm`,
        `Loop length: ${loopLengthMm}mm`,
        `Adjustment: -100mm`,
        `Total: ${poleLength}mm`
      ];
    } else {
      // Standard pole calculation (Lockhead, Springhead, Rolling Springhead)
      const loopTypeLower = loopType.toLowerCase();

      if (loopTypeLower === "cross corner loop") {
        poleLength = baseHeight + 150;
        details = [
          `Base height: ${baseHeight}mm`,
          `Cross corner addition: +150mm`,
          `Total: ${poleLength}mm`
        ];
      } else if (loopTypeLower === "corner loop") {
        let addition;

        if (loopLengthNum >= 35) {
          addition = 250;
        } else if (loopLengthNum >= 25) {
          addition = 200;
        } else if (loopLengthNum >= 15) {
          addition = 150;
        } else {
          throw new Error(`Corner loop length must be at least 15cm. Provided: ${loopLengthNum}cm.`);
        }

        poleLength = baseHeight + addition;
        details = [
          `Base height: ${baseHeight}mm`,
          `Corner loop addition (${loopLengthNum}cm): +${addition}mm`,
          `Total: ${poleLength}mm`
        ];
      } else {
        throw new Error(`Invalid loop type: "${loopType}".`);
      }
    }

    // Final validation
    if (poleLength <= 0) {
      throw new Error(`Calculated pole length (${poleLength}mm) is not valid based on inputs. Please check dimensions.`);
    }

    return {
      poleLength: `${poleLength}mm`,
      poleLengthNumeric: poleLength,
      error: null,
      details: details
    };
  } catch (error) {
    return {
      poleLength: '',
      poleLengthNumeric: 0,
      error: error.message,
      details: null
    };
  }
};

/**
 * Map calculated pole length to available size range in product catalog
 * @param {number} calculatedLength - Calculated pole length in mm
 * @param {string} poleType - Type of pole (Flow, Lockhead, Springhead, etc.)
 * @returns {string|null} Matching size range or null if no match
 */
export const mapLengthToSizeRange = (calculatedLength, poleType) => {
  // Flow poles have different ranges than standard poles
  const isFlowPole = poleType && poleType.toLowerCase().includes('flow');

  // Define available size ranges based on pole type
  const sizeRanges = isFlowPole ? [
    { range: '1000-1250mm', min: 1000, max: 1250 },
    { range: '1300-1700mm', min: 1300, max: 1700 },
    { range: '1750-2150mm', min: 1750, max: 2150 }
  ] : [
    { range: '1000-1300mm', min: 1000, max: 1300 },
    { range: '1301-1450mm', min: 1301, max: 1450 },
    { range: '1451-1650mm', min: 1451, max: 1650 },
    { range: '1651-1850mm', min: 1651, max: 1850 },
    { range: '1851-1950mm', min: 1851, max: 1950 },
    { range: '2000-2100mm', min: 2000, max: 2100 },
    { range: '2101-2250mm', min: 2101, max: 2250 },
    { range: '2251-2450mm', min: 2251, max: 2450 }
  ];

  // Find matching range
  for (const sizeRange of sizeRanges) {
    if (calculatedLength >= sizeRange.min && calculatedLength <= sizeRange.max) {
      return sizeRange.range;
    }
  }

  // If no exact match, find closest range
  let closestRange = null;
  let smallestDifference = Infinity;

  for (const sizeRange of sizeRanges) {
    const midpoint = (sizeRange.min + sizeRange.max) / 2;
    const difference = Math.abs(calculatedLength - midpoint);
    
    if (difference < smallestDifference) {
      smallestDifference = difference;
      closestRange = sizeRange.range;
    }
  }

  return closestRange;
};

/**
 * Extract pole type name from configurator pole type selection
 * @param {string} selectedPoleType - Selected pole type from step 1
 * @returns {string} Simplified pole type name for calculation
 */
export const extractPoleTypeForCalculation = (selectedPoleType) => {
  if (!selectedPoleType) return null;
  
  const poleTypeLower = selectedPoleType.toLowerCase();
  
  if (poleTypeLower.includes('lockhead')) return 'Lockhead';
  if (poleTypeLower.includes('springhead') && poleTypeLower.includes('rolling')) return 'Rolling Springhead';
  if (poleTypeLower.includes('springhead')) return 'Springhead';
  if (poleTypeLower.includes('flow')) return 'Flow';
  
  return selectedPoleType; // Return as-is if no match
};

/**
 * Check if Rolling Springhead should be recommended for small bags
 * @param {Object} params - Parameters for recommendation check
 * @param {number} params.bagLength - Bag length in cm
 * @param {number} params.bagWidth - Bag width in cm
 * @param {string} params.poleType - Current pole type selection
 * @returns {Object} Recommendation result
 */
export const checkForRollingSpringheadRecommendation = ({ bagLength, bagWidth, poleType }) => {
  // Only check if both dimensions are provided
  if (bagLength === '' || bagWidth === '' || !bagLength || !bagWidth) {
    return { shouldRecommend: false, reason: null };
  }

  const length = parseFloat(bagLength) || 0;
  const width = parseFloat(bagWidth) || 0;

  // Check if bag is small (either dimension < 95cm)
  const isSmallBag = length < 95 || width < 95;
  
  // Don't recommend if already using Rolling Springhead
  const isAlreadyRollingSpringhead = poleType && poleType.toLowerCase().includes('rolling springhead');

  if (isSmallBag && !isAlreadyRollingSpringhead) {
    return {
      shouldRecommend: true,
      reason: `We recommend using the Rolling Springhead for big bags smaller than 95 x 95. Current dimensions: ${length} x ${width}cm.`,
      recommendedPoleType: 'Rolling Springhead'
    };
  }

  return { shouldRecommend: false, reason: null };
};

/**
 * Validate inputs for pole calculation
 * @param {Object} inputs - Input values to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateCalculationInputs = ({ bagHeight, bagLength, bagWidth, loopType, loopLength, poleType }) => {
  const errors = [];
  
  // Check required fields
  if (!poleType) errors.push('Pole type is required');
  if (!bagHeight || parseFloat(bagHeight) < 1) errors.push('Valid bag height is required');
  if (!bagLength || parseFloat(bagLength) < 1) errors.push('Valid bag length is required');
  if (!bagWidth || parseFloat(bagWidth) < 1) errors.push('Valid bag width is required');
  if (!loopType) errors.push('Loop type is required');
  if (!loopLength || parseFloat(loopLength) < 15 || parseFloat(loopLength) > 50) {
    errors.push('Loop length must be between 15-50cm');
  }
  
  // Flow pole specific validation
  if (poleType && poleType.toLowerCase().includes('flow')) {
    const length = parseFloat(bagLength) || 0;
    const width = parseFloat(bagWidth) || 0;
    const loopLengthNum = parseFloat(loopLength) || 0;
    
    if (length < 95 || length > 100 || width < 95 || width > 100) {
      errors.push('Flow poles require bag dimensions between 95x95 and 100x100cm');
    }
    
    if (loopLengthNum < 20) {
      errors.push('Flow poles require minimum 20cm loop length');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};